import { IntlProvider } from 'react-intl';
import type { LayoutRouteProps, LinksFunction } from 'react-router';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from '@remix-run/react';
import { withSentry } from '@sentry/remix';

import { ToastRackProvider } from '@shieldpay/bumblebee/toast-rack';

import { shieldpayTheme } from '@shieldpay/theme-provider-ui/theme/shieldpay';
import { isHydratedRef } from '@shieldpay/utilities-ui/is-hydrated-ref';
import { useServerOnlyNonce } from '@shieldpay/wheeljack/react-router/nonce-context.ts';

import type { RootLoaderData } from './root.server';
import * as server from './root.server';

import { DevErrorBoundary } from '~/components/error-boundary/error-boundary-dev';
import { ProdErrorBoundary } from '~/components/error-boundary/error-boundary-prod';
import { messages } from '~/i18n';
import { createMeta } from '~/utilities/create-meta';

export const loader = server.loader;

export const links: LinksFunction = () => [
  {
    rel: 'apple-touch-icon',
    type: 'image/png',
    sizes: '180x180',
    href: '/favicons/apple-touch-icon.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: '/favicons/favicon-32x32.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: '/favicons/favicon-16x16.png',
  },
  { rel: 'manifest', href: '/site.webmanifest' },
  { rel: 'icon', href: '/favicon.ico' },
];

export const meta = createMeta('Shieldpay');

export const useAppRootLoaderData = () => {
  const data = useRouteLoaderData<RootLoaderData>('root');

  return { ...data, locale: data?.locale || 'en-GB' };
};

export function Layout({ children }: LayoutRouteProps) {
  const data = useAppRootLoaderData();

  const nonce = useServerOnlyNonce();

  return (
    <html lang={data.locale} ref={isHydratedRef}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />

        <Meta />
        <Links />
      </head>
      <IntlProvider locale={data.locale} messages={messages}>
        <body className={shieldpayTheme}>
          <ToastRackProvider>
            {children}
            <ScrollRestoration nonce={nonce} />
            {/* This is how we hand off the values from the server to the client. */}
            {!import.meta.env.DEV && data?.SENTRY_ENV ? (
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
              <script
                dangerouslySetInnerHTML={{
                  __html: `window.__SENTRY_VARS__ = ${JSON.stringify(data.SENTRY_ENV)}`,
                }}
                nonce={nonce}
              />
            ) : null}
            <Scripts nonce={nonce} />
          </ToastRackProvider>
        </body>
      </IntlProvider>
    </html>
  );
}

export default withSentry(Outlet);

export const ErrorBoundary = import.meta.env.DEV
  ? DevErrorBoundary
  : ProdErrorBoundary;
